import { useCallback } from 'react';
import { Path, useFormContext } from 'react-hook-form';
import { useTranslation } from '@/i18n';
import { usePastedFields } from '@/contexts/PastedFieldsContext';
import { HotelFormDataWithTranslations } from '@/i18n/translation-type-utils';
import type { HotelPensionType } from '@/types/benefits/hotel/HotelResponse';
import type { HotelSupplementPricingFormData } from '@/types/benefits/hotel/HotelSupplementSchema';
import { parseSupplementTariffBlock } from '@/utils/benefits/hotel/excelPaste/parseSupplementTariffBlock';
import type { ChildBandsCount } from '@/utils/benefits/hotel/excelPaste/types';
import ExcelPastePanel from './ExcelPastePanel';
import {
  buildLabelAndPeriodsRecap,
  buildPastedFormPeriods,
  ChildAges,
  formatRecapAmount,
  FormPeriod,
  getPeriodFieldNames,
  getPersonPriceColumns,
} from './pasteFormHelpers';
import { useExcelPasteFormatters } from './useExcelPasteFormatters';

interface SupplementTariffPasteProps {
  supplementIndex: number;
  tariffIndex: number;
  childBandsCount: ChildBandsCount;
  childAges: ChildAges;
  /** Pension types the supplement card offers as checkboxes. */
  offeredPensionTypes: HotelPensionType[];
}

const setOptions = { shouldDirty: true, shouldValidate: true };

/**
 * The grid has no supplement name, only tariff labels: the first tariff names
 * the supplement, the others only fill a supplement left without a name.
 */
const getPastedSupplementName = (
  currentName: string,
  pastedLabel: string,
  tariffIndex: number,
) =>
  tariffIndex === 0 || currentName.trim() === '' ? pastedLabel : currentName;

/**
 * Paste of a 13×3 festive supplement block (grid V3) into one supplement
 * tariff: the pension types it concerns, which replace the supplement's
 * selection, then label, periods and per-person price. The label also names
 * the supplement, as it names a pension card, see `getPastedSupplementName`.
 */
const SupplementTariffPaste = ({
  supplementIndex,
  tariffIndex,
  childBandsCount,
  childAges,
  offeredPensionTypes,
}: SupplementTariffPasteProps) => {
  const { t } = useTranslation();
  const { getValues, setValue } =
    useFormContext<HotelFormDataWithTranslations>();
  const { markPasted } = usePastedFields();
  const { formatIssue, formatPensionCodes } =
    useExcelPasteFormatters(childAges);

  const supplementPath = `hotelSupplements.${supplementIndex}`;
  const pricingListPath = `${supplementPath}.pricingList`;
  const tariffPrefix = `${pricingListPath}.${tariffIndex}`;

  const parse = useCallback(
    (text: string) => {
      const tariffs = (getValues(
        pricingListPath as Path<HotelFormDataWithTranslations>,
      ) ?? []) as HotelSupplementPricingFormData[];
      return {
        tariffs,
        result: parseSupplementTariffBlock(text, {
          childBandsCount,
          offeredPensionTypes,
          basePensionType: getValues('pricingPensionType')?.value as
            | string
            | undefined,
          otherTariffs: tariffs
            .map((tariff, index) => ({
              name: tariff.name || t('tariffs.tarif_', { index: index + 1 }),
              periods: tariff.periods ?? [],
            }))
            .filter((_, index) => index !== tariffIndex),
        }),
      };
    },
    [
      childBandsCount,
      getValues,
      offeredPensionTypes,
      pricingListPath,
      t,
      tariffIndex,
    ],
  );

  const analyze = useCallback(
    (text: string) => {
      const { tariffs, result } = parse(text);
      if (!result.value) {
        return {
          errors: result.errors.map(formatIssue),
          warnings: [],
          recap: [],
        };
      }
      const current = tariffs[tariffIndex];
      const pasted = result.value;
      const currentPensionTypes = (getValues(
        `${supplementPath}.pensionTypes` as Path<HotelFormDataWithTranslations>,
      ) ?? []) as HotelPensionType[];
      const currentSupplementName = (getValues(
        `${supplementPath}.name` as Path<HotelFormDataWithTranslations>,
      ) ?? '') as string;
      return {
        errors: [],
        warnings: result.warnings.map(formatIssue),
        recap: [
          {
            key: 'supplementName',
            label: t('hotels.pasteFromExcel.fields.supplementName'),
            current: currentSupplementName,
            pasted: getPastedSupplementName(
              currentSupplementName,
              pasted.name,
              tariffIndex,
            ),
          },
          {
            key: 'pensionTypes',
            label: t('hotels.pasteFromExcel.fields.supplementPensionTypes'),
            current: formatPensionCodes(currentPensionTypes),
            pasted: formatPensionCodes(pasted.pensionTypes),
          },
          ...buildLabelAndPeriodsRecap(
            { name: current?.name, periods: current?.periods },
            pasted,
            t,
          ),
          ...getPersonPriceColumns(childBandsCount, childAges, t).map(
            ({ field, label }) => ({
              key: `price-${field}`,
              label: t('hotels.pasteFromExcel.fields.supplementPrice', {
                person: label,
              }),
              current: formatRecapAmount(current?.pricing?.[field]),
              pasted: formatRecapAmount(pasted.pricing[field]),
            }),
          ),
        ],
      };
    },
    [
      childAges,
      childBandsCount,
      formatIssue,
      formatPensionCodes,
      getValues,
      parse,
      supplementPath,
      t,
      tariffIndex,
    ],
  );

  const apply = useCallback(
    (text: string) => {
      const { tariffs, result } = parse(text);
      const pasted = result.value;
      if (!pasted) return;

      const current = tariffs[tariffIndex];
      const periodsPath = `${tariffPrefix}.periods`;
      const currentSupplementName = (getValues(
        `${supplementPath}.name` as Path<HotelFormDataWithTranslations>,
      ) ?? '') as string;
      const supplementName = getPastedSupplementName(
        currentSupplementName,
        pasted.name,
        tariffIndex,
      );
      const set = (name: string, value: unknown) =>
        setValue(
          name as Path<HotelFormDataWithTranslations>,
          value as never,
          setOptions,
        );

      set(`${supplementPath}.name`, supplementName);
      set(`${supplementPath}.pensionTypes`, pasted.pensionTypes);
      set(`${tariffPrefix}.name`, pasted.name);
      set(
        periodsPath,
        buildPastedFormPeriods(
          current?.periods as FormPeriod[] | undefined,
          pasted.periods,
        ),
      );
      set(`${tariffPrefix}.pricing`, {
        ...current?.pricing,
        ...pasted.pricing,
      });

      markPasted([
        ...(supplementName !== currentSupplementName
          ? [`${supplementPath}.name`]
          : []),
        `${tariffPrefix}.name`,
        ...getPeriodFieldNames(periodsPath, pasted.periods.length),
        `${tariffPrefix}.pricing.adultPrice`,
        `${tariffPrefix}.pricing.adolescentPrice`,
        `${tariffPrefix}.pricing.childPrice`,
      ]);
    },
    [
      getValues,
      markPasted,
      parse,
      setValue,
      supplementPath,
      tariffIndex,
      tariffPrefix,
    ],
  );

  return <ExcelPastePanel shape="supplement" analyze={analyze} apply={apply} />;
};

export default SupplementTariffPaste;
